import React from 'react';
import clsx from 'clsx';
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';

/**
 * A headline number with its context underneath.
 *
 * `trendValue` and `subtitle` answer different questions — one is how this
 * compares with before, the other is what the number is made of — so a card
 * that has both shows both, the comparison first.
 *
 * @param {object} props
 * @param {string} props.title
 * @param {string} props.value - Already formatted; this does not do numbers.
 * @param {React.ComponentType} [props.icon]
 * @param {'up'|'down'|'neutral'} [props.trend]
 * @param {string} [props.trendValue] - The comparison line.
 * @param {string} [props.subtitle] - The breakdown line.
 * @param {'success'|'warning'|'danger'|'info'} [props.tone] - Tints the icon.
 *   Optional, and the default keeps the neutral grey square: cards that all
 *   measure the same kind of thing (pickups) read better without colour, and
 *   only earn it where the tone says something the number does not.
 */
export const StatCard = ({ title, value, icon: Icon, trend, trendValue, subtitle, tone }) => {
  return (
    <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 shadow-sm border border-border transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-text-secondary text-sm font-semibold">{title}</h3>
        {Icon && (
          <div className={clsx(
            "w-10 h-10 rounded-xl flex items-center justify-center",
            !tone && "bg-ninja-gray dark:bg-[#2d2d2d] text-text-muted",
            tone === 'success' && "bg-ninja-greenLight text-ninja-green",
            tone === 'warning' && "bg-orange-100 text-warning dark:bg-orange-900/20",
            tone === 'danger' && "bg-red-100 text-danger dark:bg-red-900/20",
            tone === 'info' && "bg-blue-100 text-info dark:bg-blue-900/20"
          )}>
            <Icon size={20} />
          </div>
        )}
      </div>
      <div className="text-3xl font-extrabold text-ninja-dark dark:text-white mb-2">
        {value}
      </div>
      {(trendValue || subtitle) && (
        <div className="space-y-1">
          {trendValue && (
            <div className="flex items-center gap-1.5 text-xs font-medium">
              {trend === 'up' && <ArrowUpRight size={14} className="text-success" />}
              {trend === 'down' && <ArrowDownRight size={14} className="text-danger" />}
              {trend === 'neutral' && <Minus size={14} className="text-text-muted" />}

              <span className={clsx(
                trend === 'up' && "text-success",
                trend === 'down' && "text-danger",
                trend === 'neutral' && "text-text-muted",
                !trend && "text-text-secondary"
              )}>
                {trendValue}
              </span>
            </div>
          )}

          {subtitle && (
            <div className="text-xs font-medium text-text-secondary">
              {subtitle}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
